import type { ReactNode } from 'react';
import clsx from 'clsx';

import { StaggerItem } from '@/shared/ui/animations';

import type { SubHeadingLevel } from './AnalysisSubHeader';
import { AnalysisMarkdown } from './AnalysisMarkdown';
import { TwoColumnProse } from './TwoColumnProse';

/** Stagger slots the card animates: the title, then the body a beat later. */
export const PROSE_CARD_STAGGER_SLOTS = 2;

interface AnalysisProseCardProps {
  title?: string;
  /**
   * `label` is a small muted label over a quote-led card; `heading` is a full-strength
   * heading that sits closer to the text it names. Defaults to `label`.
   */
  titleVariant?: 'label' | 'heading';
  /** Pull quote set above the description. */
  quote?: string;
  description: string;
  /**
   * `body` is prose. `statement` is a single emphasized claim (Key Insight, ...), set large
   * and capped at 75ch: these run long enough that a full-width line costs a reader the
   * left edge their eye returns to. Defaults to `body`.
   */
  descriptionVariant?: 'body' | 'statement';
  columns: 1 | 2;
  /**
   * Stagger index of the item before the card. When set, the title swipes up on the next
   * slot and the body on the one after (see {@link PROSE_CARD_STAGGER_SLOTS}), relative to
   * any surrounding `StaggerGroup`. Without it the card renders static.
   */
  staggerStart?: number;
  /**
   * Heading level for the title. `h2` for a sub-section directly under the section
   * heading; `h3` where it sits inside an already-titled block.
   */
  headingLevel?: SubHeadingLevel;
}

/**
 * A passage on the tinted surface, optionally led by a title and a pull quote.
 *
 * Dark mode paints `surface-raised`: the design draws its surfaces stone-700 there, but
 * Tessra's `surface` is stone-800, the same as the card the report sits on.
 */
export const AnalysisProseCard = ({
  title,
  titleVariant = 'label',
  quote,
  description,
  descriptionVariant = 'body',
  columns,
  staggerStart,
  headingLevel: Heading = 'h2',
}: AnalysisProseCardProps) => {
  const isStatement = descriptionVariant === 'statement';

  const stagger = (slot: number, node: ReactNode) =>
    staggerStart === undefined ? (
      node
    ) : (
      <StaggerItem index={staggerStart + slot} variant="swipeUp">
        {node}
      </StaggerItem>
    );

  return (
    <div
      className={clsx(
        'flex flex-col rounded-xl bg-surface px-3xl py-2xl dark:bg-surface-raised',
        isStatement ? 'gap-2' : titleVariant === 'heading' ? 'gap-3' : 'gap-8',
      )}
    >
      {title &&
        stagger(
          1,
          <Heading
            className={clsx(
              'font-medium',
              titleVariant === 'heading'
                ? 'text-heading-sm text-foreground'
                : 'text-label-md text-foreground-alt',
            )}
          >
            {title}
          </Heading>,
        )}
      {quote &&
        stagger(
          2,
          /* The marks are set here rather than stored: the quote is copied out of the prose
             character for character, and punctuation it never carried is presentation. */
          <blockquote className="max-w-[720px] border-l-2 border-foreground pl-6">
            <span className="text-pretty text-heading-md font-medium -tracking-[0.01em] text-foreground">
              {`\u201C${quote}\u201D`}
            </span>
          </blockquote>,
        )}
      {stagger(
        2,
        isStatement ? (
          <AnalysisMarkdown
            text={description}
            className="max-w-[75ch] text-heading-md font-medium text-foreground"
          />
        ) : columns === 2 ? (
          <TwoColumnProse text={description} tone="strong" />
        ) : (
          <AnalysisMarkdown
            text={description}
            className="text-pretty text-body-md text-foreground"
          />
        ),
      )}
    </div>
  );
};
